import { useTextes } from '@common/config';
import { useOrganismeDetailsContext } from '@common/contexts';
import { useCurrentCampagne } from "@common/hooks/campagne";
import {
  CampagneProjection,
  EtablissementProjection,
  OrganismeStatutEtapier, useGetAllOrganismeEtablissementsDeclarantsQuery,
  useGetAllOrganismeEtablissementsQuery,
  useGetCampagneByAnneeQuery,
  useGetOrganismeForCampagneQuery, useLazyGetOrganismeEtablissementRncpsQuery,
  useReplaceOrganismeEtablissementRncpsMutation,
  useUpdateOrganismeMutation,
} from '@common/services';
import { useLazyGetRncpsQuery } from "@common/services/src/rncp/rncp-api.service";
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from "../../../../../hooks/store.hook";
import { resetEtablissementModified } from "../stores/dashboard-stepper.slice";
import { StepStatus } from '../types/enums/step-status.enum';
import { StepBloc, StepButtonProps } from './common/StepBloc';

export const Step2Establishment = () => {
  const t = useTextes('ParcoursOfa', 'EtapierStep2');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id, siren, statutEtapier } = useOrganismeDetailsContext();

  const { campagne } = useCurrentCampagne();

  const { data: etablissements } = useGetAllOrganismeEtablissementsQuery({
    id,
    projection: EtablissementProjection.INFO,
  }, { skip: !id });

  const { data: campagnePrecedente } = useGetCampagneByAnneeQuery(
      { year: (campagne?.annee ?? 0) - 1, projection: CampagneProjection.ANNEE },
      { skip: !campagne?.annee },
  );

  const { data: organismePrecedent, isError: isErrorOrganismePrecedent } = useGetOrganismeForCampagneQuery(
      { siren, campagneId: campagnePrecedente?.id as number },
      { skip: !campagnePrecedente?.id || !siren },
  );

  const hasParticipatedPreviousCampagne = !!campagnePrecedente
      && !isErrorOrganismePrecedent
      && !!organismePrecedent;

  const { data: etablissementsPrecedents = [] } = useGetAllOrganismeEtablissementsDeclarantsQuery(
      { id: organismePrecedent?.id, projection: EtablissementProjection.INFO },
      { skip: !organismePrecedent?.id || !hasParticipatedPreviousCampagne },
  );

  const [updateOrganisme] = useUpdateOrganismeMutation();
  const [replaceRncps] = useReplaceOrganismeEtablissementRncpsMutation();
  const [getEtablissementRncps] = useLazyGetOrganismeEtablissementRncpsQuery();
  const [getRncps] = useLazyGetRncpsQuery();

  const status: StepStatus = (statutEtapier < OrganismeStatutEtapier.ETAPE_2) ? StepStatus.NEXT // "à venir" si l'étape 1 n'est pas validé
    : (statutEtapier === OrganismeStatutEtapier.ETAPE_2 && etablissements?.length === 0) ? StepStatus.NEW // "nouveau" si aucun établissement n'est saisi
      : (statutEtapier === OrganismeStatutEtapier.ETAPE_2 && etablissements?.length > 0) ? StepStatus.PROGRESS // "en cours" si au moins un établissement est saisi
        : StepStatus.DONE;

  const importRncpsFromPreviousCampagne = async () => {
    if (!hasParticipatedPreviousCampagne || !etablissements?.length) return;

    const etablissementBySiret = new Map(
        etablissements.map(e => [e.siret, e])
    );

    await Promise.all(
        etablissementsPrecedents.map(async (etabPrecedent) => {
          const etabCourant = etablissementBySiret.get(etabPrecedent.siret);
          if (!etabCourant) return;

          const { data: rncpsPrecedents } = await getEtablissementRncps({
            id: organismePrecedent.id,
            etablissementId: etabPrecedent.id,
          });
          if (!rncpsPrecedents?.length) return;

          const codes = rncpsPrecedents.map(r => r.rncp);

          const { data: rncpsCourants } = await getRncps({
            filters: {
              rncp: codes,
              "campagne.id": campagne?.id,
            },
          });

          const rncpCodeToIdCourant = new Map(
              rncpsCourants?.map(r => [r.rncp, r.id]) ?? []
          );

          const rncpsMapped = rncpsPrecedents
              .map((rncp, index) => ({
                rncpId: rncpCodeToIdCourant.get(rncp.rncp),
                ordre: index,
              }))
              .filter(r => r.rncpId !== undefined) as { rncpId: number, ordre: number }[];

          if (!rncpsMapped.length) return;

          await replaceRncps({
            id,
            etablissementId: etabCourant.id,
            body: { rncps: rncpsMapped },
          });
        })
    );
  };

  const buttonAdd: StepButtonProps = {
    label: 'Ajouter',
    onClick: () => {
      dispatch(resetEtablissementModified());
      navigate('/mon-ofa/edition/etablissement/ajout');
    },
    priority: 'primary',
  };

  const buttonEdit: StepButtonProps = {
    label: 'Modifier',
    onClick: () => {
      dispatch(resetEtablissementModified());
      navigate('/mon-ofa/edition/etablissement');
    },
    priority: 'secondary',
  };

  const buttonValidStep: StepButtonProps = {
    label: 'Étape validée',
    onClick: async () => {
      await importRncpsFromPreviousCampagne();
      await updateOrganisme({ id, dto: { statutEtapier: OrganismeStatutEtapier.ETAPE_3 } });
    },
    priority: 'secondary',
  };

  const buttons: StepButtonProps[] = [];

  switch (status) {
    case StepStatus.NEW:
      buttonAdd.label = 'Compléter mes établissements';
      buttons.push(buttonAdd);
      break;
    case StepStatus.PROGRESS:
      buttons.push(buttonAdd, buttonEdit, buttonValidStep);
      break;
    case StepStatus.DONE:
      buttons.push(buttonAdd, buttonEdit);
      break;
  }

  const titleSuffix = `- ${etablissements?.length} établissement${etablissements?.length > 1 ? 's' : ''}`;

  return (
    <StepBloc status={status}
              disabled={status === StepStatus.NEXT}
              title={(
                <div className={'flex'}>
                  <span>{t['ETAPE2_TEXTE1']}</span>
                  {(etablissements?.length > 0) &&
                    <span className={'text-(--text-label-blue-france)'}>&nbsp;{titleSuffix}</span>}
                </div>
              )}
              description={t['ETAPE2_TEXTE2']}
              buttons={buttons} />
  );
};